import { XMarkIcon } from "@heroicons/react/24/outline";
import { statusLabels } from "../../shared/customerForms";
import type { FormFilterProps } from "./formFilterTypes";

export function FormTypeFilter({ params, onChange }: Pick<FormFilterProps, "params" | "onChange">) {
  const rawType = params.get("type");
  const type = rawType === "credit" || rawType === "demo" ? rawType : "";
  return (
    <div
      role="group"
      aria-label="Form type"
      className="grid grid-cols-3 gap-1 rounded-lg bg-gray-100 p-1 sm:inline-flex"
    >
      {[
        { value: "", label: "All forms" },
        { value: "credit", label: "Credit requests" },
        { value: "demo", label: "Demo requests" },
      ].map((choice) => (
        <button
          key={choice.value}
          type="button"
          aria-label={choice.label}
          aria-pressed={(type || "") === choice.value}
          onClick={() => {
            if ((rawType || "") !== choice.value) onChange("type", choice.value);
          }}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ${(type || "") === choice.value ? "bg-white text-primary-700" : "text-gray-500 hover:text-gray-900"}`}
        >
          <span className="sm:hidden">
            {choice.value === "credit" ? "Credit" : choice.value === "demo" ? "Demo" : "All forms"}
          </span>
          <span className="hidden sm:inline">{choice.label}</span>
        </button>
      ))}
    </div>
  );
}

export default function FormFilterSummary({
  params,
  options,
  customerId,
  onChange,
  onApply,
  onClear,
}: FormFilterProps) {
  const labelFor = (key: string, value: string) => {
    if (key === "customer_id")
      return `Customer: ${options?.customers.find((option) => String(option.value) === value)?.label || value}`;
    if (key === "created_by")
      return `Submitted by: ${options?.submitters.find((option) => String(option.value) === value)?.label || value}`;
    if (key === "status")
      return `Status: ${statusLabels[value as keyof typeof statusLabels] || value}`;
    return `Search: ${value}`;
  };
  const chips = ["search", "status", "created_by", ...(!customerId ? ["customer_id"] : [])]
    .filter((key) => params.get(key))
    .map((key) => ({ key, label: labelFor(key, params.get(key)!) }));
  const from = params.get("date_from");
  const to = params.get("date_to");
  if (from || to)
    chips.push({ key: "dates", label: `Submitted: ${from || "Any date"} – ${to || "Any date"}` });
  return (
    (chips.length > 0 || params.get("type")) && (
      <div className="flex flex-wrap items-center gap-2" aria-label="Active filters">
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            aria-label={`Remove ${chip.label}`}
            onClick={() =>
              chip.key === "dates"
                ? onApply({
                    customer_id: params.get("customer_id") || "",
                    created_by: params.get("created_by") || "",
                    date_from: "",
                    date_to: "",
                  })
                : onChange(chip.key, "")
            }
            className="inline-flex max-w-full items-center gap-2 rounded-md bg-gray-100 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
          >
            <span className="truncate">{chip.label}</span>
            <XMarkIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          </button>
        ))}
        <button
          type="button"
          onClick={onClear}
          className="rounded px-1 py-1.5 text-xs font-medium text-primary-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        >
          Clear all
        </button>
      </div>
    )
  );
}
